import React, { useState } from 'react'
import Button from 'components/button'
import Input from 'components/input'
import { useApplicationStore } from 'hooks/application'
import { Link } from 'react-router-dom'
import { useAuth } from 'hooks/auth'
import {
  REGEX_SPECIAL_CHARACTERS_PASSWORD,
  ALLOWED_SPECIAL_CHARACTERS_PASSWORD,
  REGEX_PASSWORD_MIN_LENGTH,
  MIN_PASSWORD_LENGTH,
  APP_URL
} from 'app-constants'
import { isValidEmail, validateSourceUrl } from 'helpers/utils'
import { SIGN_IN } from 'routes'

export interface IFormValues {
  name: string
  email: string
  password1: string
  password2: string
}

interface Props {
  onSubmit: (values: IFormValues) => void
  template?: string
  allowedMethods?: string[]
  initialEmail?: string
  actionText?: string
}

const SignupForm: React.FC<Props> = ({ onSubmit, template, allowedMethods, initialEmail, actionText = 'Sign up' }) => {
  const { setSnackbarMessage } = useApplicationStore()
  const { startMicrosoftAuth, startGoogleAuth, startSSOAuth } = useAuth()

  const [submitting, setSubmitting] = useState<boolean>(false)
  const [values, setValues] = useState<IFormValues>({
    name: '',
    email: initialEmail || '',
    password1: '',
    password2: ''
  })

  // Default to all methods if not specified (backward compatibility)
  const methods = allowedMethods || ['google', 'microsoft', 'password']

  const nameError = values.name === ''
  const emailError = !isValidEmail(values.email) && values.email !== ''

  const passwordLengthError = !values.password1.match(REGEX_PASSWORD_MIN_LENGTH)
  const passwordLowercaseError = !values.password1.match(/^(?=.*[a-z])/)
  const passwordUppercaseError = !values.password1.match(/^(?=.*[A-Z])/)
  const passwordNumericError = !values.password1.match(/^(?=.*[0-9])/)
  const passwordSpecialError = !values.password1.match(REGEX_SPECIAL_CHARACTERS_PASSWORD)
  const confirmPasswordError = values.password1 !== values.password2
  const passwordError =
    passwordLengthError ||
    passwordLowercaseError ||
    passwordUppercaseError ||
    passwordNumericError ||
    passwordSpecialError ||
    confirmPasswordError

  const handleSubmit = async () => {
    if (nameError) {
      setSnackbarMessage({
        status: 'error',
        message: 'Enter your name.'
      })
      return
    }

    if (emailError) {
      setSnackbarMessage({
        status: 'error',
        message: 'Enter a valid email address, like name@example.com.'
      })
      return
    }

    // passwordError also covers a mismatched confirmation, which gets its own message below.
    if (passwordError && !confirmPasswordError) {
      setSnackbarMessage({
        status: 'error',
        message: `Your password needs at least ${MIN_PASSWORD_LENGTH} characters, including a lowercase letter, an uppercase letter, a number and one of these symbols: ${ALLOWED_SPECIAL_CHARACTERS_PASSWORD}`
      })
      return
    }

    if (confirmPasswordError) {
      setSnackbarMessage({
        status: 'error',
        message: "The passwords don't match. Enter the same password in both boxes."
      })
      return
    }

    setSubmitting(true)
    await onSubmit(values)
    setSubmitting(false)
  }

  const handleSignUpWithProvider = async (name: string) => {
    switch (name) {
      case 'microsoft':
        startMicrosoftAuth(template)
        break
      case 'google':
        startGoogleAuth(template)
        break
      case 'sso':
        startSSOAuth(values.email || initialEmail || '')
        break
      default:
    }
  }

  return (
    <div className="w-full">
      {methods.includes('microsoft') && (
        <Button
          internalType="outline"
          className="w-full text-base mb-2.5"
          onClick={() => handleSignUpWithProvider('microsoft')}
        >
          <img className="mr-2.5" src={validateSourceUrl(`${APP_URL}/assets/images/microsoft.svg`)} alt="microsoft" />
          {actionText} with Microsoft
        </Button>
      )}
      {methods.includes('google') && (
        <Button
          internalType="outline"
          className="w-full text-base mb-2.5 bg-surface text-body border border-divider-strong"
          onClick={() => handleSignUpWithProvider('google')}
        >
          <img className="mr-2.5" src={validateSourceUrl(`${APP_URL}/assets/images/google.svg`)} alt="google" />
          {actionText} with Google
        </Button>
      )}

      {methods.includes('saml') && (
        <Button
          internalType="outline"
          className="w-full text-base mb-2.5 bg-surface text-body border border-divider-strong"
          onClick={() => handleSignUpWithProvider('sso')}
        >
          <img className="mr-2.5" src={validateSourceUrl(`${APP_URL}/assets/images/sso.svg`)} alt="sso" width={25} />
          {actionText} with SSO
        </Button>
      )}

      {methods.includes('password') &&
        (methods.includes('google') || methods.includes('microsoft') || methods.includes('saml')) && (
          <div className="flex items-center justify-center cursor-default gap-2.5 my-4 before:content-[''] before:h-px before:w-[200px] before:bg-surface-strong after:content-[''] after:h-px after:w-[200px] after:bg-surface-strong">
            or
          </div>
        )}

      {methods.includes('password') && (
        <>
          <Input
            name="name"
            type="text"
            placeholder="Name"
            data-cy="name-input"
            autoComplete="name"
            value={values.name}
            onChange={(event) => setValues({ ...values, name: event.target.value })}
          />
          <Input
            className="mt-2.5"
            name="email"
            type="text"
            placeholder="Email"
            data-cy="email-input"
            autoComplete="email"
            value={values.email}
            onChange={(event) => setValues({ ...values, email: event.target.value })}
            disabled={!!initialEmail}
          />

          <Input
            className="mt-2.5"
            placeholder="Password"
            data-cy="password1-input"
            type="password"
            onChange={(event) => setValues({ ...values, password1: event.target.value })}
            autoComplete="new-password"
          />

          <Input
            className="mt-2.5"
            placeholder="Confirm password"
            data-cy="password2-input"
            type="password"
            autoComplete="new-password"
            onChange={(event) => setValues({ ...values, password2: event.target.value })}
          />

          <Button
            className="mt-2.5 w-full text-[1rem]"
            onClick={() => handleSubmit()}
            data-cy="signup-button"
            isLoading={submitting}
          >
            {actionText}
          </Button>
        </>
      )}

      {methods.includes('password') && (
        <div className="flex items-center justify-center mt-8">
          Already have an account?{' '}
          <Link className="ml-[5px]" to={SIGN_IN}>
            Sign in
          </Link>
        </div>
      )}

      {methods.includes('password') && (
        <div className="text-muted text-sm border-t border-divider mt-8 pt-5">
          By signing up, you agree to our
          <a className="ml-[5px] mr-[5px]" href="https://morta.io/trust/tos" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </a>
          and
          <a className="ml-[5px]" href="https://morta.io/trust/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
        </div>
      )}
    </div>
  )
}

export default SignupForm
